/**
 * Performs all the state/cache clears required when a user logs out.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS (for code reviewers asking "is this overkill?"):
 * --------------------------------------------------------------------------
 *
 * Same pattern as `buildScheduleUpdateFields`, `datesEqual`,
 * `buildDragNDropUpdateFields`, and `computeJobInitValues`: extract the
 * logout-clear orchestration into a single pure(ish) function so that tests
 * import the EXACT production code. If a future change accidentally drops
 * one of these clears, the regression test breaks — no test-helper drift.
 *
 * --------------------------------------------------------------------------
 * THE BUG THIS HELPS PREVENT (#86):
 * --------------------------------------------------------------------------
 *
 * Before the 2026-04-15 fix, `logoutUser()` cleared mongoUser and most AWS
 * query results but did NOT clear `tsUserDocument`, did NOT reset
 * `shouldFetchUser`, and did NOT flush the TanStack query cache. On same-user
 * re-login within the same browser tab:
 *   1. TanStack served the cached user doc (same `id` as the pre-logout doc)
 *   2. The ID-equality gate in useEffect[userResult] (modelCx.context.js
 *      ~line 4973) evaluated false → setTsUserDocument never fired
 *   3. useEffect[tsUserDocument] therefore never re-ran
 *   4. setupUserSettings() never applied the user's defaultLocation /
 *      defaultTab
 *   5. User saw whatever screen was visible pre-logout
 *
 * Closing and reopening the browser worked because every piece of React state
 * and every TanStack cache entry was wiped. This helper ensures logout has
 * the same effect as a fresh browser session for the purpose of re-login
 * routing.
 *
 * Each clear below has a specific role — see inline comments.
 */

/**
 * Runs every state/ref/cache clear required for a clean logout.
 *
 * @param {Object} deps - injected setters, refs, and queryClient. All are
 *   required; leaving any undefined will throw.
 * @param {Function} deps.setMongoUser
 * @param {Function} deps.setAwsgwUserResult
 * @param {Function} deps.setAwsgwProjectsResult
 * @param {Function} deps.setAwsgwCandidatesResult
 * @param {Function} deps.setAwsgwAttributesResult
 * @param {Function} deps.setAwsgwUniqueLocationsResult
 * @param {Function} deps.setAwsgwOfficeMetaDataResult
 * @param {Function} deps.setTsUserDocument
 * @param {Function} deps.setShouldFetchUser
 * @param {{ clear: Function }} deps.queryClient
 * @param {{ current: boolean }} deps.hasRunSetupUserSettingsRef
 * @param {{ current: boolean }} deps.hasAppliedUserSettingsRef
 * @param {{ current: boolean }} deps.hasTriedCreateUserRef - #90 ref that
 *   guards the auto-create-user path against the race that produced
 *   ~27,936 duplicate User docs. Reset here so re-login within the same
 *   browser tab correctly re-enables the path for the next user if they
 *   truly don't have a record yet. Without resetting this, a first-time
 *   user who logs in AFTER someone else logged out would fail to get a
 *   user doc created (the guard would still be set to true from the
 *   previous session).
 */
export function performLogoutClears(deps) {
  const {
    setMongoUser,
    setAwsgwUserResult,
    setAwsgwProjectsResult,
    setAwsgwCandidatesResult,
    setAwsgwAttributesResult,
    setAwsgwUniqueLocationsResult,
    setAwsgwOfficeMetaDataResult,
    setTsUserDocument,
    setShouldFetchUser,
    queryClient,
    hasRunSetupUserSettingsRef,
    hasAppliedUserSettingsRef,
    hasTriedCreateUserRef,
  } = deps;

  // MSAL user reference — must null out so App.js login effect re-fires on
  // next login (it gates on `account && !mongoUser`).
  setMongoUser(null);

  // AWS-GW query results that a logged-out session should not retain.
  setAwsgwUserResult({});
  setAwsgwProjectsResult([]);
  setAwsgwCandidatesResult([]);
  setAwsgwAttributesResult([]);
  setAwsgwUniqueLocationsResult({});
  setAwsgwOfficeMetaDataResult({});

  // #86 load-bearing clear: without this, the ID-equality gate in
  // useEffect[userResult] blocks setTsUserDocument on re-login.
  setTsUserDocument({});

  // #86: reset the query-enable flag so getUserByEmail refetches after login.
  setShouldFetchUser(false);

  // #86 defense-in-depth: flush every TanStack cache entry so re-login can't
  // serve the previous user's cached data, regardless of query key or gcTime.
  queryClient.clear();

  // Reset user-settings application tracking so next login re-applies
  // defaultLocation / defaultTab via setupUserSettings().
  hasRunSetupUserSettingsRef.current = false;
  hasAppliedUserSettingsRef.current = false;

  // #90: Reset the auto-create-user guard so a different user logging in
  // on the same browser tab can have their record created if needed.
  // The guard is expected to be re-set once by the useEffect the moment it
  // fires createNewUser for the new user.
  if (hasTriedCreateUserRef) {
    hasTriedCreateUserRef.current = false;
  }
}
